import { useRouter } from 'next/router';

import { cleanUrlString } from 'Utilities/helpers/CleanUrlString/cleanUrlString';
import ROUTES from 'Utilities/routes';

import type { PageNavigationState } from '../PageComposerSiteNavigation.types';

const {
	CONTACT_US,
	COOKIES,
	DEALER_WALLET,
	DELIVERY,
	INDEX,
	MANAGE_SAVED_SEARCH,
	MANAGE_SAVED_SEARCH_ADD,
	MANAGE_SAVED_SEARCH_CONFIRMATION,
	MANAGE_SAVED_SEARCH_DELETE,
	MODULR_PAYMENT_TERMS,
	PAYMENT_TERMS,
	PRICING,
	PRIVACY,
	SIGN_IN,
	TERMS,
	VEHICLE_LIST,
} = ROUTES;

type IdParsedFromQuery = string | string[] | undefined;

interface GetPageNavigationStateParams {
	enquiryId: IdParsedFromQuery;
	slug: IdParsedFromQuery;
}

interface GetPageNavigationConfig {
	[key: string]: Pick<PageNavigationState, 'hasBottomBorder' | 'isSticky' | 'isTransparent'>;
}

const routesToHideNavigation = [
	MANAGE_SAVED_SEARCH.as(),
	MANAGE_SAVED_SEARCH_ADD.as(),
	MANAGE_SAVED_SEARCH_CONFIRMATION.as(),
	MANAGE_SAVED_SEARCH_DELETE.as(),
];

const getPageNavigationConfig = ({ enquiryId, slug }: GetPageNavigationStateParams): GetPageNavigationConfig => {
	const slugValue = Array.isArray(slug) ? slug[0] : slug;
	const defaultState = { hasBottomBorder: false, isSticky: false, isTransparent: true };
	const sticky = { ...defaultState, isSticky: true, isTransparent: false };
	const vehicleListState = { ...defaultState, isTransparent: false };

	const pathsWithSticky = [
		INDEX.as(),
		SIGN_IN.as(),
		PRICING.as(),
		DELIVERY.as(Number(enquiryId)),
		PAYMENT_TERMS.as(),
		CONTACT_US.as(),
		TERMS.as(),
		PRIVACY.as(),
		COOKIES.as(),
		MODULR_PAYMENT_TERMS.as(),
		DEALER_WALLET.as(slugValue),
	];

	const stickyPathsConfig = Object.fromEntries(pathsWithSticky.map((path) => [path, sticky]));

	return {
		default: defaultState,
		...stickyPathsConfig,
		[DEALER_WALLET.as(slugValue)]: { ...sticky, hasBottomBorder: true },
		// VEHICLE_LIST.as() = '/vehicles' (client-side asPath) but on SSR Next.js
		// provides the internal href '/vehicle_list' as asPath. Both must match.
		[VEHICLE_LIST.as()]: vehicleListState,
		[VEHICLE_LIST.href]: vehicleListState,
	};
};

const getNavigationState = (config: GetPageNavigationConfig, asPathUrl: string): PageNavigationState => {
	const isHidden = routesToHideNavigation.includes(asPathUrl);
	const currentPageState = config[asPathUrl] || config.default;

	return { ...currentPageState, isHidden };
};

export const useNavigationState = (): PageNavigationState => {
	const { asPath, query } = useRouter();
	const { enquiryId, slug } = query;
	const asPathUrl = cleanUrlString(asPath);
	const config = getPageNavigationConfig({ enquiryId, slug });

	return getNavigationState(config, asPathUrl);
};
